/**
 * Auth primitives for the learn app (SCH Milestone A).
 *
 * Thin wrappers over `auth.getCurrentUser` / `auth.logout` so screens
 * never hand-roll the gate:
 *
 *   - `useCurrentUser()` — the raw query. `data === undefined` while
 *     loading, `data === null` when signed out, else the user object.
 *   - `<RequireAuth>` — renders children only when authed; centered
 *     spinner while loading; redirects to `/login` when definitively
 *     unauthenticated.
 *   - `useSignOut()` — logout mutation + full client-cache wipe
 *     + navigate to `/login`.
 *   - `wipeClientCaches()` — the §3.3 shared-device cache wipe (Slice 1b):
 *     drops the in-memory query cache AND the persisted IndexedDB bucket.
 *     Consumer audience (likely minors) on shared/cyber-café devices —
 *     a prior user's analytics/profile/transcripts must never survive a
 *     user switch.
 *
 * `.tsx` (not `.ts`) because `<RequireAuth>` is JSX.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import { createOfflinePersister } from '@mangrove/ui';
import { clearClientCache } from '@mangrove/trpc-client/react';
import { trpc, type AppRouter } from './trpc.js';
import { CenteredLoading } from '../components/loading.js';

/**
 * The one IndexedDB bucket this app persists its query cache into.
 * `main.tsx` builds the app persister from this constant, and
 * `wipeClientCaches` purges the same bucket — single source of truth so
 * the wipe can never drift away from the persist target.
 */
export const LEARN_CACHE_DB_NAME = 'mangrove-learn-cache';

let wipeInFlight: Promise<void> | null = null;

/**
 * Shared-device cache wipe (SCH-AI-1b, plan §3.3): `queryClient.clear()`
 * (drops every cached query AND any paused offline mutations queued by the
 * previous user) + remove the persisted IndexedDB bucket via the
 * persister's own `removeClient()` — `createOfflinePersister(dbName)` is
 * pure over its dbName, so a fresh handle deletes the exact key/store the
 * app persister writes (`mangrove.tanstack-query` in `query-cache` of
 * `mangrove-learn-cache`). We deliberately do NOT
 * `indexedDB.deleteDatabase(...)` — the live persister holds an open
 * connection to that database, which would block deletion.
 *
 * Idempotent + re-entrant: concurrent calls (double-submit, StrictMode
 * double-invoke) share one in-flight promise, and deleting an
 * already-empty bucket is a no-op. AWAIT the returned promise before
 * navigating so the purge can't race the next screen's persist/restore.
 */
export function wipeClientCaches(queryClient: QueryClient): Promise<void> {
  if (wipeInFlight) return wipeInFlight;
  // The shared wipe every app uses (#777): cancel in-flight reads, remove the
  // persisted client, then clear memory, in the order that keeps a persist
  // tick from re-writing stale data between the two.
  wipeInFlight = clearClientCache({
    queryClient,
    persister: createOfflinePersister(LEARN_CACHE_DB_NAME),
  }).finally(() => {
    wipeInFlight = null;
  });
  return wipeInFlight;
}

/**
 * The signed-in shape of `auth.getCurrentUser`:
 * `{ userId, tenantId, name, email, tenantName, storeType, branding, isCofounder }`.
 * The procedure returns `null` for anonymous callers.
 */
export type CurrentUser = NonNullable<inferRouterOutputs<AppRouter>['auth']['getCurrentUser']>;

/**
 * Portable subset of the tRPC query result. The full generic result type
 * isn't nameable from app code (`@trpc/react-query` is hoisted under
 * `@mangrove/trpc-client` → TS2742), so we annotate with the fields the
 * shell + gates actually use. Screens that need more (`isFetching`,
 * `status`, …) can call `trpc.auth.getCurrentUser.useQuery()` directly.
 */
export interface CurrentUserQueryResult {
  /** `undefined` while loading, `null` when signed out, else the user. */
  data: CurrentUser | null | undefined;
  isPending: boolean;
  isError: boolean;
  error: { message: string } | null;
  refetch: () => Promise<unknown>;
}

/**
 * Wraps `trpc.auth.getCurrentUser.useQuery()`. Read it as:
 * `isPending` → still resolving; `data === null` → signed out;
 * `data` truthy → signed in.
 */
export function useCurrentUser(): CurrentUserQueryResult {
  return trpc.auth.getCurrentUser.useQuery();
}

/** Display name fallback chain shared by the shell + screens. */
export function displayNameOf(user: CurrentUser): string {
  return user.name ?? user.email ?? `user ${user.userId.slice(0, 8)}`;
}

/**
 * The learner's FIRST name, for the top bar (Ishraf §3.7 — "Ishraf", not
 * "Ishraf Hossain"). A full name crowds the 56px bar on a narrow screen and
 * a short greeting reads better; the full name still shows on /profile.
 *
 * Falls back exactly like `displayNameOf` so a user with no name still gets
 * something stable: the email local-part, then a short id.
 */
export function firstNameOf(user: CurrentUser): string {
  const first = user.name?.trim().split(/\s+/)[0];
  if (first) return first;
  const local = user.email?.split('@')[0];
  return local ?? `user ${user.userId.slice(0, 8)}`;
}

/**
 * Auth gate. Same semantics as the operator SPAs' index gates: redirect
 * happens in an effect (never during render), and the brief
 * post-redirect frame renders empty.
 */
export function RequireAuth({ children }: { children: React.ReactNode }): React.JSX.Element {
  const navigate = useNavigate();
  const me = useCurrentUser();

  React.useEffect(() => {
    if (!me.isPending && me.data === null) {
      void navigate({ to: '/login', replace: true });
    }
  }, [me.isPending, me.data, navigate]);

  if (me.isPending) return <CenteredLoading />;
  if (!me.data) return <></>;
  return <>{children}</>;
}

/**
 * Sign-out: `auth.logout` (clears the session cookie server-side), then
 * the §3.3 shared-device cache wipe — memory + IndexedDB purged and
 * AWAITED **before** navigating to `/login`, so the next user on this
 * device can never render this user's cached data. The wipe's
 * `queryClient.clear()` also resets `getCurrentUser`, so every gate
 * re-evaluates (the old explicit invalidate is subsumed). `onSettled`
 * (not `onSuccess`) so a failed network call still resets the client view.
 */
export function useSignOut(): { signOut: () => void; isPending: boolean } {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = trpc.auth.logout.useMutation({
    onSettled: async () => {
      await wipeClientCaches(queryClient);
      void navigate({ to: '/login', replace: true });
    },
  });
  return {
    signOut: () => logout.mutate(),
    isPending: logout.isPending,
  };
}
